/**
 * assets/cart-drawer.js
 * Slide-out cart. Opens after any add to cart (product form, Quick Add on
 * cards, Build-a-Box) and when the header cart icon is clicked. Every
 * change goes through Shopify's Ajax Cart API, then the drawer is
 * re-rendered with the Section Rendering API so totals, discounts and the
 * free-shipping bar always match the real cart.
 *
 * Events:
 *   listens  dyet:cart:added   → refresh + open
 *   emits    dyet:cart:refresh → cart.js updates the header badge / cart page
 */
(function () {
  const settings = (window.driedYet && window.driedYet.settings) || {};
  const root = settings.rootUrl || "/";
  const SECTION = "cart-drawer";
  let lastFocus = null;

  function drawer() {
    return document.querySelector("[data-cart-drawer]");
  }

  function isOpen() {
    const d = drawer();
    return d && d.classList.contains("show");
  }

  async function render() {
    const res = await fetch(`${root}?sections=${SECTION}`);
    if (!res.ok) return;
    const data = await res.json();
    const html = data[SECTION];
    if (!html) return;
    const wasOpen = isOpen();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const next = doc.querySelector("[data-cart-drawer]");
    const current = drawer();
    if (next && current) {
      if (wasOpen) {
        next.classList.add("show");
        next.setAttribute("aria-hidden", "false");
      }
      current.replaceWith(next);
    }
    document.dispatchEvent(new CustomEvent("dyet:cart:refresh"));
  }

  function open() {
    const d = drawer();
    if (!d) return false;
    lastFocus = document.activeElement;
    d.classList.add("show");
    d.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("drawer-open");
    const panel = d.querySelector(".cart-drawer");
    if (panel) panel.focus();
    return true;
  }

  function close() {
    const d = drawer();
    if (!d) return;
    d.classList.remove("show");
    d.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("drawer-open");
    if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus();
  }

  async function addVariant(variantId, quantity, button) {
    const label = button ? button.textContent : "";
    if (button) {
      button.disabled = true;
      button.textContent = "Adding…";
    }
    try {
      const res = await fetch(settings.cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || "Couldn't add that item.");
      document.dispatchEvent(
        new CustomEvent("dyet:add_to_cart", { detail: { variant_id: variantId, quantity: quantity || 1, source: "quick_add" } })
      );
      await render();
      open();
    } catch (err) {
      if (button) {
        button.textContent = err.message;
        setTimeout(() => (button.textContent = label), 2500);
      }
      return;
    } finally {
      if (button) {
        button.disabled = false;
        if (button.textContent === "Adding…") button.textContent = label;
      }
    }
  }

  async function changeLine(line, quantity) {
    await fetch(settings.cartChangeUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)) }),
    });
    if (Number(quantity) === 0) {
      document.dispatchEvent(new CustomEvent("dyet:remove_from_cart", { detail: { line, quantity: 0 } }));
    }
    await render();
    const firstBtn = drawer() && drawer().querySelector("[data-drawer-qty], [data-drawer-close]");
    if (firstBtn) firstBtn.focus();
  }

  let noteTimer = null;
  function saveNote(value) {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(settings.cartUpdateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ note: value }),
      });
    }, 400);
  }

  document.addEventListener("click", (e) => {
    const quick = e.target.closest("[data-card-quick-add]");
    if (quick) {
      e.preventDefault();
      addVariant(quick.dataset.variantId, 1, quick);
      return;
    }

    const qty = e.target.closest("[data-drawer-qty]");
    if (qty) {
      changeLine(qty.dataset.line, qty.dataset.qty);
      return;
    }

    if (e.target.closest("[data-drawer-close]")) {
      close();
      return;
    }

    // click on the dimmed backdrop (outside the panel) closes
    const d = drawer();
    if (d && e.target === d) {
      close();
      return;
    }

    // Header cart icon opens the drawer instead of leaving the page
    // (the cart page itself keeps the normal link).
    const cartLink = e.target.closest(".cart-icon-btn");
    if (cartLink && !document.querySelector("[data-cart-section]") && open()) {
      e.preventDefault();
    }
  });

  document.addEventListener("input", (e) => {
    if (e.target.matches("[data-cart-note]")) saveNote(e.target.value);
  });

  document.addEventListener("keydown", (e) => {
    if (!isOpen()) return;
    if (e.key === "Escape") {
      close();
      return;
    }
    if (e.key === "Tab") {
      // keep focus inside the open drawer
      const focusables = Array.from(
        drawer().querySelectorAll('a[href], button:not([disabled]), textarea, input, summary, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener("dyet:cart:added", async () => {
    await render();
    open();
  });

  window.driedYetCartDrawer = { open, close, render };
})();
